import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment-timezone';
import type Specialist from '../../model/Specialist';
import type AvailableTime from '../../model/AvailableTime';
import { AppointmentType } from '../../model';
import { availabilitiesApi } from '../../services/availabilities';
import { useAppDispatch } from '../../hooks/redux-hooks';
import type { AppDispatch } from '../../redux/store';
import {
  dayHasFutureSlot as dayHasFutureBookableSlot,
  getEarliestFutureSlotMoment,
  parseAppointmentLengthMinutes,
} from '../../utils/expand-timeslots';

export { dayHasFutureSlot } from '../../utils/expand-timeslots';

const MAX_MONTH_LOOKAHEAD = 3;
const MONTH_FORMAT = 'YYYYMM';
const DISPLAY_FORMAT = 'DD/MM/YYYY HH:mm';
const AVAILABILITY_STALE_THRESHOLD_MS = 2 * 60 * 1000;

export interface SpecialistAvailability {
  isLoading: boolean
  availableTimeByMonth: Record<string, AvailableTime[]>
}

const DEFAULT_AVAILABILITY: SpecialistAvailability = {
  isLoading: true,
  availableTimeByMonth: {},
};

type AvailabilityMap = Record<string, SpecialistAvailability>

interface FetchResult {
  timeByMonth: Record<string, AvailableTime[]>
}

export interface AvailabilityRefreshCompletion {
  specialistId: string
  month: string
  completedAt: number
}

export interface AvailabilityLoadFailure {
  specialistId: string
  month: string
  failedAt: number
}

export interface UseSpecialistAvailabilitiesReturn {
  getAvailability: (specialistId: string) => SpecialistAvailability
  loadMonth: (specialistId: string, month: string) => void
  lastRefreshCompletion: AvailabilityRefreshCompletion | null
  lastLoadFailure: AvailabilityLoadFailure | null
}

const hasFutureSlot = (
  list: AvailableTime[],
  appointmentLengthMinutes: number,
): boolean => list.some((day) => dayHasFutureBookableSlot(day, appointmentLengthMinutes));

export const getEarliestSlot = (
  availability: SpecialistAvailability,
  appointmentLengthMinutes?: number,
): moment.Moment | null => {
  const days = Object.keys(availability.availableTimeByMonth)
    .sort()
    .flatMap((month) => availability.availableTimeByMonth[month]);
  return getEarliestFutureSlotMoment(days, appointmentLengthMinutes);
};

export const getEarliestLabel = (
  availability: SpecialistAvailability,
  appointmentLengthMinutes?: number,
): string | null => {
  const earliest = getEarliestSlot(availability, appointmentLengthMinutes);
  return earliest ? earliest.format(DISPLAY_FORMAT) : null;
};

const fetchAvailabilitiesForMonth = (
  dispatch: AppDispatch,
  specialistId: string,
  appointmentType: AppointmentType,
  month: string,
): Promise<AvailableTime[] | undefined> => {
  const request = dispatch(
    availabilitiesApi.endpoints.getAvailabilitiesBySpecialist.initiate(
      {
        specialistId,
        appointmentType,
        month,
      },
      { forceRefetch: true },
    ),
  );

  return request
    .then(({ data, error }) => {
      if (error) return undefined;
      return data ?? [];
    })
    .finally(() => {
      request.unsubscribe();
    });
};

const fetchAvailabilityWithFallback = (
  dispatch: AppDispatch,
  specialistId: string,
  appointmentType: AppointmentType,
  appointmentLengthMinutes: number,
  onMonthFailed: (month: string) => void,
): Promise<FetchResult> => {
  const months = Array.from(
    { length: MAX_MONTH_LOOKAHEAD },
    (_, i) => moment().add(i, 'months').format(MONTH_FORMAT),
  );

  return months.reduce<Promise<FetchResult>>(
    (chain, month) => chain.then((accumulated) => {
      const hasAvailability = Object.values(accumulated.timeByMonth)
        .some((list) => hasFutureSlot(list, appointmentLengthMinutes));
      if (hasAvailability) return accumulated;

      return fetchAvailabilitiesForMonth(
        dispatch,
        specialistId,
        appointmentType,
        month,
      ).then((days) => {
        if (days === undefined) {
          onMonthFailed(month);
          return accumulated;
        }
        return {
          timeByMonth: { ...accumulated.timeByMonth, [month]: days },
        };
      });
    }),
    Promise.resolve({ timeByMonth: {} }),
  );
};

const useSpecialistAvailabilities = (
  specialists: Specialist[] | undefined,
  appointmentType: AppointmentType,
): UseSpecialistAvailabilitiesReturn => {
  const dispatch = useAppDispatch();
  const [availabilityMap, setAvailabilityMap] = useState<AvailabilityMap>({});
  const [lastRefreshCompletion, setLastRefreshCompletion] = useState<
    AvailabilityRefreshCompletion | null
  >(null);
  const [lastLoadFailure, setLastLoadFailure] = useState<AvailabilityLoadFailure | null>(null);
  const fetchedAtRef = useRef<Map<string, number>>(new Map());
  const inFlightMonthsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!specialists?.length) return undefined;

    let isCancelled = false;
    const specialistIds = specialists.map((s) => s.id);

    // The lines below wipe availableTimeByMonth, so the matching freshness
    // stamps must go too, otherwise loadMonth would treat a wiped month as
    // fresh and never refetch it.
    specialistIds.forEach((id) => {
      const keyPrefix = `${id}-`;
      fetchedAtRef.current.forEach((_fetchedAt, key) => {
        if (key.startsWith(keyPrefix)) {
          fetchedAtRef.current.delete(key);
        }
      });
    });

    setAvailabilityMap((prev) => {
      const next = { ...prev };
      specialistIds.forEach((id) => {
        next[id] = { isLoading: true, availableTimeByMonth: {} };
      });
      return next;
    });

    specialistIds.forEach((id) => {
      const specialist = specialists.find((s) => s.id === id);
      const appointmentLengthMinutes = parseAppointmentLengthMinutes(specialist?.appointmentLength);

      const recordFailure = (month: string) => {
        if (isCancelled) return;
        setLastLoadFailure({ specialistId: id, month, failedAt: Date.now() });
      };

      fetchAvailabilityWithFallback(dispatch, id, appointmentType, appointmentLengthMinutes, recordFailure)
        .then(({ timeByMonth }) => {
          if (isCancelled) return;

          Object.keys(timeByMonth).forEach((month) => {
            fetchedAtRef.current.set(`${id}-${month}`, Date.now());
          });

          // Merge, never replace: loadMonth may already have written a month
          // that this lookahead skipped because its request failed.
          setAvailabilityMap((prev) => {
            const existing = prev[id] ?? DEFAULT_AVAILABILITY;
            return {
              ...prev,
              [id]: {
                isLoading: false,
                availableTimeByMonth: {
                  ...existing.availableTimeByMonth,
                  ...timeByMonth,
                },
              },
            };
          });
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [specialists, appointmentType, dispatch]);

  const getAvailability = useCallback(
    (specialistId: string): SpecialistAvailability => availabilityMap[specialistId] ?? DEFAULT_AVAILABILITY,
    [availabilityMap],
  );

  const loadMonth = useCallback((specialistId: string, month: string) => {
    const key = `${specialistId}-${month}`;
    if (inFlightMonthsRef.current.has(key)) return;

    const lastFetchedAt = fetchedAtRef.current.get(key);
    const isFresh = lastFetchedAt !== undefined
      && Date.now() - lastFetchedAt < AVAILABILITY_STALE_THRESHOLD_MS;
    if (isFresh) return;

    inFlightMonthsRef.current.add(key);

    fetchAvailabilitiesForMonth(
      dispatch,
      specialistId,
      appointmentType,
      month,
    ).then((days) => {
      if (days === undefined) {
        setLastLoadFailure({ specialistId, month, failedAt: Date.now() });
        return;
      }

      fetchedAtRef.current.set(key, Date.now());
      // React 17 does not batch updates dispatched from a promise callback, so
      // the data must land before the completion signal that consumers react to.
      setAvailabilityMap((prev) => {
        const existing = prev[specialistId] ?? DEFAULT_AVAILABILITY;
        return {
          ...prev,
          [specialistId]: {
            ...existing,
            availableTimeByMonth: {
              ...existing.availableTimeByMonth,
              [month]: days,
            },
          },
        };
      });
      setLastRefreshCompletion({
        specialistId,
        month,
        completedAt: Date.now(),
      });
    }).finally(() => {
      inFlightMonthsRef.current.delete(key);
    });
  }, [dispatch, appointmentType]);

  return {
    getAvailability, loadMonth, lastRefreshCompletion, lastLoadFailure,
  };
};

export default useSpecialistAvailabilities;
